"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { useAuthErrorMessages } from "@saas/auth/hooks/errors-messages";
import { OrganizationInvitationAlert } from "@saas/organizations/components/OrganizationInvitationAlert";
import { useFormErrors } from "@shared/hooks/form-errors";
import { Alert, AlertDescription, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@ui/components/form";
import { Input } from "@ui/components/input";
import {
	AlertTriangleIcon,
	ArrowRightIcon,
	EyeIcon,
	EyeOffIcon,
	MailboxIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { withQuery } from "ufo";
import { z } from "zod";
import {
	type OAuthProvider,
	oAuthProviders,
} from "../constants/oauth-providers";
import { SocialSigninButton } from "./SocialSigninButton";

const formSchema = z.object({
	email: z.string().email(),
	password: z.string().min(1),
	name: z.string().min(1),
	subscribeToNewsletter: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

export function SignupForm({ prefillEmail }: { prefillEmail?: string }) {
	const t = useTranslations();
	const router = useRouter();
	const { zodErrorMap } = useFormErrors();
	const { getAuthErrorMessage } = useAuthErrorMessages();
	const searchParams = useSearchParams();

	const [showPassword, setShowPassword] = useState(false);
	const invitationId = searchParams.get("invitationId");
	const email = searchParams.get("email");
	const redirectTo = searchParams.get("redirectTo");
	const plan = searchParams.get("plan");
	const interval = searchParams.get("interval");

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema, {
			errorMap: zodErrorMap,
		}),
		values: {
			name: "",
			email: prefillEmail ?? email ?? "",
			password: "",
			subscribeToNewsletter: true,
		},
	});

	const invitationOnlyMode = !config.auth.enableSignup && invitationId;
	// Any signup that arrived via an invitation link. The server auto-verifies these
	// users (the invite link already proves email ownership), so we can sign them in
	// and accept the invitation immediately instead of making them check their inbox.
	const isInvitationSignup = !!invitationId;
	const isAgencyPlan = plan && plan.includes("agency");

	const onSubmit: SubmitHandler<FormValues> = async ({
		email,
		password,
		name,
		subscribeToNewsletter,
	}) => {
		try {
			// When the email is locked to an invitation, always submit that address —
			// never a value the user could have edited — so the created account
			// matches the invitation's recipient.
			const signupEmail = prefillEmail ?? email;

			// Store plan selection and newsletter preference in localStorage to persist through auth flow
			// better-auth doesn't support query params in callbackURL
			// Using localStorage (not sessionStorage) because email verification opens in new tab
			if (typeof window !== 'undefined') {
				const signupData = {
					plan: plan || undefined,
					interval: interval || 'month',
					subscribeToNewsletter,
					timestamp: Date.now(),
				};
				localStorage.setItem('signup-plan-data', JSON.stringify(signupData));
			}

			const { error } = await authClient.signUp.email({
				email: signupEmail,
				password,
				name,
				callbackURL: invitationId
					? `/app/organization-invitation/${invitationId}`
					: (redirectTo ?? config.auth.redirectAfterSignIn),
			});

			if (error) {
				throw error;
			}

			if (invitationOnlyMode || isInvitationSignup) {
				// Invited users are auto-verified server-side, so sign them in (the
				// signup response carries no session when requireEmailVerification is
				// on) and accept the invitation before redirecting into the app.
				if (!invitationOnlyMode) {
					const { error: signInError } = await authClient.signIn.email({
						email: signupEmail,
						password,
					});
					if (signInError) {
						throw signInError;
					}
				}
				const { data: accepted } =
					await authClient.organization.acceptInvitation({
						invitationId,
					});
				// Persist the joined org as the session's active organization.
				// Nothing else sets it for invited users (the signup hook skips
				// them), and API routes that scope by session.activeOrganizationId
				// fail with "No active organization" until it is set.
				if (accepted?.member?.organizationId) {
					await authClient.organization.setActive({
						organizationId: accepted.member.organizationId,
					});
				}
				router.push(config.auth.redirectAfterSignIn);
			}
		} catch (e) {
			form.setError("root", {
				message: getAuthErrorMessage(
					e && typeof e === "object" && "code" in e
						? (e.code as string)
						: undefined,
				),
			});
		}
	};

	return (
		<div>
			<h1 className="font-bold text-2xl md:text-3xl text-center">
				{isAgencyPlan ? t("auth.signup.titleAgency") : t("auth.signup.title")}
			</h1>
			<p className="mt-1 mb-6 text-foreground/60 text-center">
				{isAgencyPlan ? t("auth.signup.messageAgency") : t("auth.signup.message")}
			</p>

			{form.formState.isSubmitSuccessful && !invitationOnlyMode && !isInvitationSignup ? (
				<Alert variant="success">
					<MailboxIcon className="size-6" />
					<AlertTitle>
						{t("auth.signup.hints.verifyEmail")}
					</AlertTitle>
				</Alert>
			) : (
				<>
					{invitationId && (
						<OrganizationInvitationAlert className="mb-6" />
					)}

					<Form {...form}>
						<form
							className="flex flex-col items-stretch gap-4"
							onSubmit={form.handleSubmit(onSubmit)}
						>
							{form.formState.isSubmitted &&
								form.formState.errors.root && (
									<Alert variant="error">
										<AlertTriangleIcon className="size-6" />
										<AlertDescription>
											{form.formState.errors.root.message}
										</AlertDescription>
									</Alert>
								)}

							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("auth.signup.name")}
										</FormLabel>
										<FormControl>
											<Input {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("auth.signup.email")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												autoComplete="email"
												readOnly={!!prefillEmail}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("auth.signup.password")}
										</FormLabel>
										<FormControl>
											<div className="relative">
												<Input
													type={
														showPassword
															? "text"
															: "password"
													}
													className="pr-10"
													{...field}
													autoComplete="new-password"
												/>
												<button
													type="button"
													onClick={() =>
														setShowPassword(
															!showPassword,
														)
													}
													className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary text-xl"
												>
													{showPassword ? (
														<EyeOffIcon className="size-4" />
													) : (
														<EyeIcon className="size-4" />
													)}
												</button>
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="subscribeToNewsletter"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="font-normal text-sm cursor-pointer">
												{t("auth.signup.newsletterOptIn")}
											</FormLabel>
										</div>
									</FormItem>
								)}
							/>

							<div className="text-foreground/60 text-xs text-center">
								{t.rich("auth.signup.termsAgreement", {
									termsLink: (chunks) => (
										<Link
											href="/legal/terms"
											className="text-primary hover:underline"
											target="_blank"
											rel="noopener noreferrer"
										>
											{chunks}
										</Link>
									),
									privacyLink: (chunks) => (
										<Link
											href="/legal/privacy-policy"
											className="text-primary hover:underline"
											target="_blank"
											rel="noopener noreferrer"
										>
											{chunks}
										</Link>
									),
								})}
							</div>

							<Button loading={form.formState.isSubmitting}>
								{isAgencyPlan ? t("auth.signup.submitAgency") : t("auth.signup.submit")}
							</Button>
						</form>
					</Form>

					{config.auth.enableSignup &&
						config.auth.enableSocialLogin && (
							<>
								<div className="relative my-6 h-4">
									<hr className="relative top-2" />
									<p className="-translate-x-1/2 absolute top-0 left-1/2 mx-auto inline-block h-4 bg-card px-2 text-center font-medium text-foreground/60 text-sm leading-tight">
										{t("auth.login.continueWith")}
									</p>
								</div>

								<div className="grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2">
									{Object.keys(oAuthProviders).map(
										(providerId) => (
											<SocialSigninButton
												key={providerId}
												provider={
													providerId as OAuthProvider
												}
											/>
										),
									)}
								</div>
							</>
						)}
				</>
			)}

			<div className="mt-6 text-center text-sm">
				<span className="text-foreground/60">
					{t("auth.signup.alreadyHaveAccount")}{" "}
				</span>
				<Link
					href={withQuery(
						"/auth/login",
						Object.fromEntries(searchParams.entries()),
					)}
				>
					{t("auth.signup.signIn")}
					<ArrowRightIcon className="ml-1 inline size-4 align-middle" />
				</Link>
			</div>
		</div>
	);
}
